import { siteText } from '../siteText'
import { cloneElement, useEffect, useLayoutEffect, useRef, useState, type ComponentProps, type ReactElement } from 'react'
import { createPortal } from 'react-dom'

/** Portal out of the scrolling navigation so labels cannot be clipped. */
export function IconTooltip({ label, side = 'right', children }: { label: string; side?: 'right' | 'bottom'; children: ReactElement<ComponentProps<'button'>> }) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const [position, setPosition] = useState({ left: 0, top: 0 })
  const tip = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  function hide() { clearTimeout(timer.current); setAnchor(null) }
  function show(target: HTMLElement, immediate = false) {
    clearTimeout(timer.current)
    const reveal = () => setAnchor(target.getBoundingClientRect())
    if (immediate) reveal()
    else timer.current = setTimeout(reveal, 180)
  }
  useEffect(() => {
    const dismiss = (event: KeyboardEvent) => { if (event.key === 'Escape') hide() }
    window.addEventListener('keydown', dismiss)
    window.addEventListener('resize', hide)
    window.addEventListener('scroll', hide, true)
    return () => {
      clearTimeout(timer.current)
      window.removeEventListener('keydown', dismiss)
      window.removeEventListener('resize', hide)
      window.removeEventListener('scroll', hide, true)
    }
  }, [])
  useLayoutEffect(() => {
    if (!anchor || !tip.current) return
    const { width, height } = tip.current.getBoundingClientRect()
    const left = side === 'right' ? anchor.right + 10 : anchor.right - width
    const top = side === 'right' ? anchor.top + (anchor.height - height) / 2 : anchor.bottom + 8
    setPosition({ left: Math.max(8, Math.min(left, window.innerWidth - width - 8)), top: Math.max(8, Math.min(top, window.innerHeight - height - 8)) })
  }, [anchor, label, side])
  return <>{cloneElement(children, {
    title: undefined,
    onPointerEnter: event => { children.props.onPointerEnter?.(event); if (event.pointerType !== 'touch') show(event.currentTarget) },
    onPointerLeave: event => { children.props.onPointerLeave?.(event); hide() },
    onFocus: event => { children.props.onFocus?.(event); if (event.currentTarget.matches(':focus-visible')) show(event.currentTarget, true) },
    onBlur: event => { children.props.onBlur?.(event); hide() },
    onClick: event => { hide(); children.props.onClick?.(event) },
  })}{anchor ? createPortal(<div ref={tip} className="motion-icon-tooltip" style={position} aria-hidden="true">{siteText(label)}</div>, document.body) : null}</>
}
